/**
 * Todo lo que se detecta en el sitio, en una sola lista.
 *
 * Espejo de `services/hallazgos/` del servidor. Los tipos se copian tal cual,
 * incluido `Origen`, que distingue "no hay hallazgos de velocidad" de "no medí
 * la velocidad". Aplanar esa distinción acá sería deshacer del lado del
 * navegador lo que el servidor cuida.
 *
 * **La clasificación NO se recalcula acá.** `accion` y `porQueNo` vienen
 * decididos del servidor por la misma razón que `arreglable` viaja con cada
 * hallazgo de accesibilidad: una copia de la regla en el frontend se separa de
 * la del servidor en la primera corrección, y la que manda es la del servidor.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.js';

export type FuenteHallazgo = 'accesibilidad' | 'seo' | 'velocidad';
export type GravedadHallazgo = 'alta' | 'media' | 'baja';
export type AccionHallazgo = 'automatico' | 'texto' | 'bloqueado' | 'diagnostico';

export interface Hallazgo {
  id: string;
  fuente: FuenteHallazgo;
  regla: string;
  gravedad: GravedadHallazgo;
  titulo: string;
  detalle: string;
  cuantos: number;
  accion: AccionHallazgo;
  porQueNo: string | null;
  targetId: string | null;
  ayudaUrl: string | null;
  /** Para velocidad: qué arreglo automático lo cubre. */
  arreglo?: 'imagenes' | 'fuentes' | 'cache' | 'bloqueo' | 'video' | 'contraste' | 'hreflang' | 'encabezados' | 'lcp';
}

/** Las métricas resultado de velocidad, como Lighthouse las escribe ("7,6 s"). */
export interface MetricasDeVelocidad { fcp?: string; lcp?: string; tbt?: string; cls?: string; si?: string }

export type Origen =
  | { fuente: FuenteHallazgo; medido: true; cuando: string; hallazgos: Hallazgo[]; auditId?: number; metricas?: MetricasDeVelocidad }
  | { fuente: FuenteHallazgo; medido: false; porque: string };

export interface ResumenHallazgos {
  total: number;
  automatico: number;
  texto: number;
  bloqueado: number;
  diagnostico: number;
  alta: number;
  media: number;
  baja: number;
}

/** Un hallazgo cerrado sin tocar el archivo. Sigue vigente en la página. */
export interface HallazgoDescartado {
  id: string;
  titulo: string;
  motivo: string;
  cuando: string;
}

/** En qué quedó un borrador que se guardó (la revisión semanal o un clic). */
export type EstadoDelBorrador = 'propuesta' | 'bien' | 'decidir' | 'error' | 'aplicado';
export interface BorradorGuardado {
  hallazgoId: string;
  check: string;
  targetId: string | null;
  tipo: string;
  estado: EstadoDelBorrador;
  borrador: string | null;
  porque: string | null;
  material: MaterialDelBorrador | null;
  comoAplicar: ComoAplicar;
  reviewId: number | null;
  actualizadoEn: string;
}

export interface RespuestaHallazgos {
  origenes: Origen[];
  hallazgos: Hallazgo[];
  resumen: ResumenHallazgos;
  descartados: HallazgoDescartado[];
  /** Los borradores guardados, por id de hallazgo: la fila arranca en ese estado. */
  borradores: Record<string, BorradorGuardado>;
  /** Los tres números de la cabecera. `null` es "no medido", y se dibuja como
   *  un guion: un cero ahí diría que el sitio sacó cero. */
  puntajes: { accesibilidad: number | null; seo: number | null; velocidad: number | null };
}

export interface Pieza { de: string; texto: string }
export interface MaterialDelBorrador {
  tipo: string;
  piezas: Pieza[];
  destinosPosibles?: Array<{ ruta: string; titulo: string }>;
  altActual?: string | null;
  idiomaDelSitio: string | null;
}

export type ComoAplicar =
  | { via: 'pagina'; tipo: 'titulo' | 'descripcion' | 'idioma' | 'canonical'; valor: string }
  | { via: 'edicion'; op: Record<string, unknown> }
  | null;

export type RespuestaBorrador =
  | { sePuede: true; decorativa: true; porque: string; material: MaterialDelBorrador; comoAplicar?: ComoAplicar }
  | { sePuede: true; borrador: string; material: MaterialDelBorrador; comoAplicar: ComoAplicar }
  /** `comoAplicar` viene igual: si Evo Brain no puede decidir, Eduardo escribe
   *  el texto y lo aplica por el mismo camino. `fallo` distingue "no puedo
   *  decidirlo" de "el modelo falló": lo segundo se reintenta. */
  | { sePuede: false; porque: string; material?: MaterialDelBorrador; comoAplicar?: ComoAplicar; fallo?: boolean };

const base = (projectId: number) => `/api/projects/${String(projectId)}`;

/**
 * La lista. `conVelocidad` gasta cuota diaria de PageSpeed, así que arranca
 * apagada: abrir la pestaña de Diseño para mirar otra cosa no tiene por qué
 * consumirla. El botón de medir la enciende.
 */
export function useHallazgos(projectId: number, conVelocidad: boolean) {
  return useQuery<RespuestaHallazgos>({
    queryKey: ['hallazgos', projectId, conVelocidad],
    queryFn: () => api.get(`${base(projectId)}/hallazgos${conVelocidad ? '?velocidad=1' : ''}`),
    // Una medición no cambia sola: la cambia publicar o arreglar, y las dos
    // invalidan esta clave.
    staleTime: 5 * 60_000,
  });
}

export function useRedactarBorrador(projectId: number) {
  return useMutation({
    mutationFn: ({ check, targetId, hallazgoId }: { check: string; targetId: string | null; hallazgoId: string }) =>
      api.post<RespuestaBorrador>(`${base(projectId)}/hallazgos/redactar`, { check, targetId, hallazgoId }),
  });
}

/** El estado de un borrador guardado: aplicado, o el texto corregido a mano. */
export function useMarcarBorrador(projectId: number) {
  return useMutation({
    mutationFn: ({ hallazgoId, estado, borrador }: { hallazgoId: string; estado: EstadoDelBorrador; borrador?: string }) =>
      api.put<{ ok: true }>(`${base(projectId)}/hallazgos/borradores/${encodeURIComponent(hallazgoId)}`, { estado, ...(borrador !== undefined ? { borrador } : {}) }),
  });
}

/** Cierra un hallazgo sin tocar el archivo: "está bien así", "no aplica". */
export function useDescartarHallazgo(projectId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (h: { hallazgoId: string; titulo: string; motivo: string }) =>
      api.post<{ descartado: HallazgoDescartado }>(`${base(projectId)}/hallazgos/descartar`, h),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['hallazgos', projectId] }); },
  });
}

export function useRestaurarHallazgo(projectId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (hallazgoId: string) =>
      api.delete<{ ok: true }>(`${base(projectId)}/hallazgos/descartar?hallazgoId=${encodeURIComponent(hallazgoId)}`),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['hallazgos', projectId] }); },
  });
}

/** Qué hallazgo resuelve una aplicación. Va al reporte del cliente. */
export interface HallazgoResuelto {
  check: string;
  titulo: string;
  tipo: string;
  antes?: string | null;
  despues: string;
}

/** Aplica un cambio de cabecera: título, descripción o idioma. */
export function useAplicarEnPagina(projectId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (cambio: { tipo: string; valor: string; hallazgo?: HallazgoResuelto; hallazgoId?: string }) =>
      api.post<{ cambio: boolean; anterior: string | null; respaldo: string | null }>(
        `${base(projectId)}/hallazgos/pagina`, cambio,
      ),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['hallazgos', projectId] }); },
  });
}

export interface ResultadoOptimizacion {
  imagenes: { procesadas: number; omitidas: number; bytesAntes: number; bytesDespues: number; archivos: string[] };
  lazy: number;
  fontDisplay: number;
  respaldo: string | null;
}

/** Recomprime y achica las imágenes de la copia de trabajo, con respaldo. Tarda. */
export function useOptimizarImagenes(projectId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<ResultadoOptimizacion>(`${base(projectId)}/velocidad/optimizar-imagenes`, {}),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['periodo', projectId] }); },
  });
}

/** Caché del navegador y compresión: un bloque en el .htaccess de la copia de trabajo. */
export function useAgregarCache(projectId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<{ ok: true; cambiado: boolean; respaldo: string | null }>(`${base(projectId)}/velocidad/cache`, {}),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['periodo', projectId] }); },
  });
}

export interface ResultadoContraste {
  ok: true; ajustados: number; revisados: number; sinAncla: number; portadas: number; respaldo: string | null; puntaje: number | null;
}

/** El contraste medido en Chromium y corregido como ajuste del editor; vuelve a auditar. */
export function useAjustarContraste(projectId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<ResultadoContraste>(`${base(projectId)}/accesibilidad/contraste`, {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['hallazgos', projectId] });
      void qc.invalidateQueries({ queryKey: ['periodo', projectId] });
    },
  });
}

export interface ResultadoLcp {
  ok: true; cambiado: boolean; respaldo: string | null;
  medida: { tipo: 'img' | 'fondo' | 'texto' | 'video' | 'ninguno'; url: string | null };
}

/** La imagen LCP medida en Chromium y pedida desde el primer byte. */
export function useArreglarLcp(projectId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<ResultadoLcp>(`${base(projectId)}/velocidad/lcp`, {}),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['periodo', projectId] }); },
  });
}

/** Solo las fuentes y la carga diferida en el HTML: rápido, no toca imágenes. */
export function useArreglarFuentes(projectId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<ResultadoOptimizacion>(`${base(projectId)}/velocidad/fuentes`, {}),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['periodo', projectId] }); },
  });
}

export interface ResultadoCarga {
  cssInlineado: string[];
  fuentes: number;
  video: number;
  motivoVideo: 'api-de-youtube' | null;
  respaldo: string | null;
}

/** Lo que frena el primer dibujo (bloqueo) o el video de YouTube después de la página (video). */
export function useOptimizarCarga(projectId: number, que: 'bloqueo' | 'video') {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<ResultadoCarga>(`${base(projectId)}/velocidad/${que}`, {}),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['periodo', projectId] }); },
  });
}
